import { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { fetchZones, createZone, updateZone, deleteZone } from "../../api/ZoneService";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";
import { Visibility, Add, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import theme from "../../theme/theme";
import { CreateZoneDialog } from "../../components/zone/CreateZoneDialog";
import { EditZoneDialog } from "../../components/zone/EditZoneDialog";

const ZonesManagementPage = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState({});
  const [openEdit, setOpenEdit] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const loadZones = async () => {
    setLoading(true);
    try {
      const response = await fetchZones();
      setZones(response.data.data ? response.data.data : response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch zones");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateZone = async (zoneData) => {
    setCreating(true);
    setErrors({});
    try {
      await createZone(zoneData);
      toast.success("Zone created successfully");
      setOpenCreate(false);
      loadZones();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to create zone");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEditZone = async (zoneData) => {
    setEditing(true);
    setEditErrors({});
    try {
      await updateZone(editingZone.id, zoneData);
      toast.success("Zone updated successfully");
      setOpenEdit(false);
      setEditingZone(null);
      loadZones();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setEditErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to update zone");
      }
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteClick = (zone) => {
    setDeleteTarget(zone);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteZone(deleteTarget.id);
      if (res?.status === "error") {
        toast.error(res.message || "Failed to delete zone");
      } else {
        toast.success("Zone deleted successfully");
        setConfirmOpen(false);
        setDeleteTarget(null);
        loadZones();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to delete zone");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadZones();
  }, []);

  const columns = [
    { field: "name", headerName: "Zone Name" },
    { field: "code", headerName: "Zone Code" },
    {
      field: "cities_count",
      headerName: "Assigned Cities",
      renderCell: (row) => row.cities_count || 0,
    },
    {
      field: "projects_count",
      headerName: "Total Projects",
      renderCell: (row) => row.projects_count || 0,
    },
  ];

  const actions = [
    {
      icon: <Visibility />,
      label: "View Cities",
      onClick: (row) => {
        navigate(`/zones/${row.id}`);
      },
      backgroundColor: theme.button.viewBackgroundColor,
    },
    {
      icon: <Edit />,
      label: "Edit Zone",
      onClick: (row) => {
        setEditingZone(row);
        setEditErrors({});
        setOpenEdit(true);
      },
      backgroundColor: theme.button.editBackgroundColor || "#f0a500",
    },
    {
      icon: <Delete />,
      label: "Delete Zone",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor || "#ff5252",
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Zones Management
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            sx={{
              borderRadius: theme.button.borderRadius,
              backgroundColor: theme.palette.primary.main,
            }}
            onClick={() => setOpenCreate(true)}
          >
            Add Zone
          </Button>
        </Box>
      </Box>
      <Box>
        <TableComponent
          data={zones || []}
          columns={columns}
          actions={actions}
          loading={loading}
          links={[]}
          pagination={false}
        />
      </Box>
      <CreateZoneDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        saving={creating}
        onSave={handleCreateZone}
        errors={errors}
      />
      <EditZoneDialog
        open={openEdit}
        onClose={() => { setOpenEdit(false); setEditingZone(null); }}
        saving={editing}
        onSave={handleEditZone}
        zone={editingZone}
        errors={editErrors}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Zone"
        message={`Are you sure you want to delete zone "${deleteTarget?.name}"? Zones with assigned cities or projects cannot be deleted.`}
        loading={deleting}
      />
    </Box>
  );
};

export default ZonesManagementPage;
